import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, EASE, fmtUSD } from "../lib/site";

export const PledgeModal = ({ open, amount, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        ...form,
        amount,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Could not start secure checkout", {
        description: "Please check your details and try again.",
      });
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-[28px] border border-line bg-cardx p-8 shadow-2xl"
            data-testid="pledge-modal"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-inksoft">
                  Your donation
                </p>
                <h3 className="mt-2 font-serif text-3xl font-semibold text-ink">
                  {fmtUSD(amount)}
                </h3>
              </div>
              <button
                data-testid="btn-close-pledge-modal"
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-inksoft transition-colors hover:border-brand hover:text-brand"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form data-testid="form-pledge-modal" onSubmit={submit} className="mt-6 space-y-3">
              <input
                data-testid="input-pledge-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="input-field"
              />
              <input
                data-testid="input-pledge-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                className="input-field"
              />
              <textarea
                data-testid="input-pledge-message"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="A note with your gift (optional)"
                className="input-field resize-none"
              />
              <button
                data-testid="btn-submit-pledge"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDFBF7] transition-colors hover:bg-branddeep disabled:opacity-50"
              >
                <Lock className="h-3.5 w-3.5" />
                {loading ? "Redirecting to Stripe…" : `Continue to secure payment · ${fmtUSD(amount)}`}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-inksoft">
                You'll complete this gift on Stripe's secure checkout. Test mode:
                card 4242 4242 4242 4242, any future date, any CVC.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
